import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  getTranslation,
} from '@resy/ui-kitchen';

import moment from 'moment-timezone';

import ReservationCancel from './ReservationCancel';
import ReservationCanceled from './ReservationCanceled';
import ReservationCancelError from './ReservationCancelError';

import Close from '../../svg/dynamic/close.svg';
import Spinner from '../../../images/icons/processing-spinner.svg';

import './ReservationCancelModal.scss';

const ReservationCancelModal = ({
  $location,
  closeModal,
  cancelReservation,
  errors,
  isCanceled,
  isMobile,
  isOpen: initialIsOpen,
  reservation,
}) => {
  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.querySelector('.ReactModal__Content')?.focus();
  }, []);

  const getStyles = () => (
    {
      content: {
        border: 'none',
        borderRadius: isMobile ? '0' : '5px',
        maxWidth: isMobile ? '768px' : '700px',
        padding: '0',
        inset: isMobile ? '0' : '20vh 40px auto',
      },
    }
  );

  const handleCloseModal = () => {
    const search = $location.search();
    $location.search({
      ...search,
      resy_token: null,
    });
    closeModal();
    setIsOpen(false);
  };

  const handleCancelReservation = () => {
    setLoading(true);
    cancelReservation();
  };

  const renderHeader = () => (
    <div className="ReservationCancelModal__head">
      <Button
        className="ReservationCancelModal__Close"
        onClick={handleCloseModal}
        variant="no-style"
        data-test-id="reservation_cancel-button-close"
        aria-label={getTranslation({ translationKey: 'close', fallback: 'Close' })}
      >
        <Close width={15} height={15} />
      </Button>
    </div>
  );

  const renderLoading = () => {
    if (reservation || errors) return null;

    return (
      <div
        className="ReservationCancelModal__content"
      >
        <div
          role="alert"
          aria-label={getTranslation({
            translationKey: 'communication_loading_message',
            fallback: 'Loading...',
          })}
        />
        <Spinner
          className="Icon--loading"
          aria-hidden="true"
          width={33}
          height={32}
        />
      </div>
    );
  };

  const renderErrors = () => {
    if (!errors) return null;

    return (<ReservationCancelError closeModal={handleCloseModal} />);
  };

  const renderCanceled = () => {
    if (!isCanceled) return null;

    const { venue: { name, location: { urlSlug }, venueSlug } } = reservation;

    const search = $location.search();
    $location.search({
      ...search,
      resy_token: null,
    });

    return (
      <ReservationCanceled
        venue={name}
        url={
          `/cities/${urlSlug}/venues/${venueSlug}?date=${moment().format('YYYY-MM-DD')}&seats=2`
        }
        closeModal={handleCloseModal}
      />
    );
  };

  const renderReservation = () => {
    if (!reservation || isCanceled || errors) return null;

    const {
      cancellationPolicy, day, time, tableType, seats, venue: {
        name, images,
      },
    } = reservation;

    return (
      <ReservationCancel
        closeModal={handleCloseModal}
        cancelReservation={handleCancelReservation}
        seats={seats}
        tableType={tableType}
        timeslot={{ day, time }}
        venue={name}
        backgroundImage={(images?.length > 0) ? images[0] : null}
        cancellationPolicy={cancellationPolicy}
        loading={loading}
      />
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      onRequestClose={handleCloseModal}
      styles={getStyles()}
    >
      <div
        className="ReservationCancelModal"
      >
        {renderHeader()}
        {renderLoading()}
        {renderErrors()}
        {renderCanceled()}
        {renderReservation()}
      </div>
    </Modal>
  );
};

ReservationCancelModal.propTypes = {
  $location: PropTypes.shape({
    search: PropTypes.func.isRequired,
  }).isRequired,
  closeModal: PropTypes.func,
  cancelReservation: PropTypes.func,
  errors: PropTypes.shape({
    data: {
      status: PropTypes.number,
    },
  }),
  isCanceled: PropTypes.bool,
  isMobile: PropTypes.bool,
  isOpen: PropTypes.bool,
  reservation: PropTypes.shape({
    isCanceled: PropTypes.bool,
    cancellationPolicy: PropTypes.arrayOf(PropTypes.string),
    day: PropTypes.string,
    time: PropTypes.string,
    tableType: PropTypes.string,
    seats: PropTypes.number,
    venue: PropTypes.shape({
      images: PropTypes.arrayOf(PropTypes.string),
      location: PropTypes.shape({
        code: PropTypes.string,
        urlSlug: PropTypes.string.isRequired,
      }),
      name: PropTypes.string,
      venueSlug: PropTypes.string,
    }),
  }),
};

ReservationCancelModal.defaultProps = {
  cancelReservation: () => {},
  closeModal: () => {},
  errors: null,
  isMobile: false,
  isOpen: true,
  isCanceled: false,
  reservation: null,
};

export default ReservationCancelModal;
